import React, { useState } from 'react';
import SearchableTicker from '../SearchableTicker';
import ExpirationDateSelect from '../Watchlist/ExpirationDateSelect';
import StrikePriceSelect from '../Watchlist/StrikePriceSelect';

const emptyRow = () => ({
  tickerOption: null,
  ticker: '',
  option_type: 'call',
  expiration: '',
  strike: '',
  quantity: '',
  cost_basis: '',
});

/**
 * HedgingForm — enter the options you OWN plus a protection goal, then solve for
 * the protective-put hedge that caps your loss on a target downside move.
 *
 * Mirrors ElasticityForm's Tailwind styling. Holdings rows reuse the existing
 * SearchableTicker / ExpirationDateSelect / StrikePriceSelect with the same
 * cascading-clear behavior as WatchlistParamsForm.
 */
function HedgingForm({ onSubmit, processing }) {
  const [rows, setRows] = useState([emptyRow()]);
  const [params, setParams] = useState({
    target_move_pct: -10,
    horizon_days: 30,
    loss_cap: '',
    loss_cap_type: 'dollar',
    trim_pct: 15, // percent in the UI, converted to a fraction on submit
  });

  const updateRow = (index, changes) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...changes } : r)));
  };

  const handleTickerChange = (index, option) => {
    // Ticker change clears expiration AND strike (cascading dependency).
    updateRow(index, {
      tickerOption: option,
      ticker: option ? option.value : '',
      expiration: '',
      strike: '',
    });
  };

  const handleOptionTypeChange = (index, type) => {
    // Option type change clears strike only.
    updateRow(index, { option_type: type, strike: '' });
  };

  const handleExpirationChange = (index, value) => {
    // Expiration change clears strike only.
    updateRow(index, { expiration: value, strike: '' });
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const rowIsValid = (r) => r.ticker && r.expiration && r.strike;

  const handleSubmit = (e) => {
    e.preventDefault();

    const validRows = rows.filter(rowIsValid);
    if (validRows.length === 0) {
      alert('Please enter at least one holding (ticker, expiration, and strike).');
      return;
    }
    if (!params.loss_cap || Number(params.loss_cap) <= 0) {
      alert('Please enter a loss cap greater than 0.');
      return;
    }

    const owned_contracts = validRows.map((r) => {
      const contract = {
        ticker: r.ticker,
        option_type: r.option_type,
        strike: Number(r.strike),
        expiration: r.expiration,
        quantity: Number(r.quantity) || 1,
      };
      if (r.cost_basis !== '' && r.cost_basis != null) {
        contract.cost_basis = Number(r.cost_basis);
      }
      return contract;
    });

    onSubmit({
      owned_contracts,
      target_move_pct: Number(params.target_move_pct),
      horizon_days: Number(params.horizon_days) || 30,
      loss_cap: Number(params.loss_cap),
      loss_cap_type: params.loss_cap_type,
      trim_fraction: (Number(params.trim_pct) || 0) / 100,
    });
  };

  const labelCls = 'block text-sm font-medium text-gray-300 mb-2';
  const inputCls =
    'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-2">Hedge / Protect a Position</h2>
        <p className="text-gray-400">
          Enter the options you own, a downside move to protect against, and the most you're
          willing to lose. We'll solve for the protective puts that cap that loss — and show a
          trim-and-redeploy alternative that frees capital while keeping some upside.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Holdings */}
        <div>
          <label className={labelCls}>Your Holdings *</label>
          <div className="space-y-4">
            {rows.map((row, index) => (
              <div
                key={index}
                className="bg-gray-900/40 border border-gray-700 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-400">
                    Holding #{index + 1}
                  </span>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className="text-red-400 hover:text-red-300 text-xs"
                      disabled={processing}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Ticker */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Ticker</label>
                    <SearchableTicker
                      value={row.tickerOption}
                      onChange={(opt) => handleTickerChange(index, opt)}
                      placeholder="Search ticker..."
                    />
                  </div>

                  {/* Option type */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Option Type</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleOptionTypeChange(index, 'call')}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          row.option_type === 'call'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                        disabled={processing}
                      >
                        Call
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOptionTypeChange(index, 'put')}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          row.option_type === 'put'
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                        disabled={processing}
                      >
                        Put
                      </button>
                    </div>
                  </div>

                  {/* Expiration */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Expiration</label>
                    <ExpirationDateSelect
                      ticker={row.ticker}
                      value={row.expiration}
                      onChange={(value) => handleExpirationChange(index, value)}
                      disabled={processing || !row.ticker}
                    />
                  </div>

                  {/* Strike */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Strike</label>
                    <StrikePriceSelect
                      ticker={row.ticker}
                      optionType={row.option_type}
                      expiration={row.expiration}
                      value={row.strike}
                      onChange={(value) => updateRow(index, { strike: value })}
                      disabled={processing}
                    />
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1"># of Contracts</label>
                    <input
                      type="number"
                      min="1"
                      value={row.quantity}
                      onChange={(e) => updateRow(index, { quantity: e.target.value })}
                      placeholder="e.g. 7"
                      className={inputCls}
                      disabled={processing}
                    />
                  </div>

                  {/* Cost basis */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Avg Cost / Contract (optional)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.cost_basis}
                      onChange={(e) => updateRow(index, { cost_basis: e.target.value })}
                      placeholder="premium paid"
                      className={inputCls}
                      disabled={processing}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addRow}
            className="mt-3 px-3 py-2 text-xs rounded bg-gray-700 text-gray-200 hover:bg-gray-600"
            disabled={processing}
          >
            + Add Holding
          </button>
        </div>

        {/* Protection parameters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Protect Against (move %)</label>
            <input
              type="number"
              value={params.target_move_pct}
              onChange={(e) => setParams((p) => ({ ...p, target_move_pct: e.target.value }))}
              className={inputCls}
              disabled={processing}
            />
            <p className="text-xs text-gray-500 mt-1">e.g. -10 = a 10% drop</p>
          </div>

          <div>
            <label className={labelCls}>Horizon (days)</label>
            <input
              type="number"
              min="1"
              max="365"
              value={params.horizon_days}
              onChange={(e) => setParams((p) => ({ ...p, horizon_days: e.target.value }))}
              className={inputCls}
              disabled={processing}
            />
            <p className="text-xs text-gray-500 mt-1">Short-term protection window</p>
          </div>

          <div>
            <label className={labelCls}>Max Acceptable Loss</label>
            <input
              type="number"
              min="0"
              value={params.loss_cap}
              onChange={(e) => setParams((p) => ({ ...p, loss_cap: e.target.value }))}
              placeholder={params.loss_cap_type === 'dollar' ? 'e.g. 20000' : 'e.g. 10'}
              className={inputCls}
              disabled={processing}
            />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setParams((p) => ({ ...p, loss_cap_type: 'dollar' }))}
                className={`flex-1 px-2 py-1 text-xs rounded ${
                  params.loss_cap_type === 'dollar'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                disabled={processing}
              >
                $ Dollars
              </button>
              <button
                type="button"
                onClick={() => setParams((p) => ({ ...p, loss_cap_type: 'percent' }))}
                className={`flex-1 px-2 py-1 text-xs rounded ${
                  params.loss_cap_type === 'percent'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                disabled={processing}
              >
                % of Position
              </button>
            </div>
          </div>

          <div>
            <label className={labelCls}>Trim Fraction (%)</label>
            <input
              type="number"
              min="0"
              max="95"
              value={params.trim_pct}
              onChange={(e) => setParams((p) => ({ ...p, trim_pct: e.target.value }))}
              className={inputCls}
              disabled={processing}
            />
            <p className="text-xs text-gray-500 mt-1">
              % of position to sell in the trim-and-redeploy model
            </p>
          </div>
        </div>

        {/* Info box */}
        <div className="bg-gray-700/50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">How this works</h4>
          <ul className="text-xs text-gray-400 space-y-1">
            <li>• We value your position across downside scenarios using Black-Scholes.</li>
            <li>• Then we find the cheapest protective puts that keep your loss at or under your cap.</li>
            <li>• The <strong>trim-and-redeploy</strong> view sells part of the position to free capital,
              buys puts on the rest, and adds a small short-dated call sleeve to retain upside.</li>
          </ul>
        </div>

        {/* Submit */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={processing}
            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {processing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Building hedge...
              </div>
            ) : (
              'Find My Hedge'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default HedgingForm;
